import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eyebrow } from '../ui';
import { AlertIcon, AppIcon, AppsIcon, BookIcon, CardsIcon, ChartIcon, CloudIcon, DataSetIcon, DevicesIcon, GaugeIcon, GlassIcon, GlobeIcon, HeartIcon, ListIcon, LocationIcon, MenuWatchIcon, OverviewIcon, PhoneLinkIcon, PlayIcon, SatelliteIcon, SettingsIcon, ShipIcon, StarIcon, SwitchviewIcon, TidesIcon, TouchIcon, TrophyIcon, WeatherIcon, WrenchIcon, } from '../ui/GuideIcons';
import './SectionNav.css';
/** Icon je Abschnitt, erkannt am (englischen) Schlüssel in der Abschnitts-ID */
const ICON_RULES = [
    // Spiel-Abschnitte (Voidrunner), exakt, damit andere Anleitungen unberührt bleiben
    [/^hud$/, HeartIcon],
    [/^upgrades$/, CardsIcon],
    [/^ships$/, ShipIcon],
    [/^progress$/, TrophyIcon],
    [/^galaxies$/, StarIcon],
    [/^endless$/, GaugeIcon],
    [/^ranking$/, GlobeIcon],
    [/trouble|need|support/i, WrenchIcon],
    [/warning/i, AlertIcon],
    [/intro|overview/i, OverviewIcon],
    [/watch-menu|menu/i, MenuWatchIcon],
    [/glance|complication/i, AppsIcon],
    [/setting|aqstd|interval|limits|comp|series/i, SettingsIcon],
    [/device|instinct/i, DevicesIcon],
    [/location|gps/i, LocationIcon],
    [/search|connection/i, SatelliteIcon],
    [/connect|activity/i, PhoneLinkIcon],
    [/model|weather/i, WeatherIcon],
    [/next-6h|day-detail|forecast|measure|bac|index|values|chart/i, ChartIcon],
    [/pages|views|switch/i, SwitchviewIcon],
    [/nav|control|button|touch|refresh/i, TouchIcon],
    [/start|during|stop|after/i, PlayIcon],
    [/drink/i, GlassIcon],
    [/title|diff|eu$|us$/i, ListIcon],
    [/sky|aurora|meteor|iss|moon|planet/i, StarIcon],
    [/arc|exp|more/i, SwitchviewIcon],
    [/body|move|goal/i, ChartIcon],
    [/tide/i, TidesIcon],
    [/air/i, CloudIcon],
    [/data/i, DataSetIcon],
    [/app/i, AppIcon],
];
/** Erwartet den Abschnittsschlüssel ohne Präfix (z. B. "day-detail" aus "atmosweather-day-detail") */
const iconFor = (key) => ICON_RULES.find(([re]) => re.test(key))?.[1] || BookIcon;
const keyOf = (domId) => domId.replace(/^[^-]+-/, '');
/**
 * Untermenü für lange Produkt-Abschnitte (App-Anleitungen): liest die Abschnitte (section[id] mit h3)
 * aus dem Inhalt, zeigt sie links als mitlaufende Liste mit Icons (Desktop) bzw. als Chips (mobil)
 * und setzt vor jede Überschrift das passende Icon.
 */
const SectionNav = ({ children, min = 4 }) => {
    const { t, i18n } = useTranslation();
    const ref = useRef(null);
    const [items, setItems] = useState([]);
    const [active, setActive] = useState('');
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const read = () => {
            const next = Array.from(el.querySelectorAll('section[id]'))
                .map((s) => ({ id: s.id, label: s.querySelector('h3')?.textContent?.trim() || '' }))
                .filter((i) => i.label);
            setItems((prev) => prev.length === next.length && prev.every((p, i) => p.id === next[i].id && p.label === next[i].label) ? prev : next);
        };
        read();
        const mo = new MutationObserver(read);
        mo.observe(el, { childList: true, subtree: true, characterData: true });
        return () => mo.disconnect();
    }, [i18n.language]);
    useEffect(() => {
        if (items.length < min)
            return;
        const onScroll = () => {
            let current = items[0].id;
            for (const i of items) {
                const s = document.getElementById(i.id);
                if (s && s.getBoundingClientRect().top < 190)
                    current = i.id;
            }
            setActive(current);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [items, min]);
    const go = (e, id) => {
        e.preventDefault();
        const s = document.getElementById(id);
        if (s)
            window.scrollTo({ top: window.scrollY + s.getBoundingClientRect().top - 150, behavior: 'smooth' });
    };
    const show = items.length >= min;
    return (_jsxs("div", { className: `section-nav-wrap ${show ? 'has-nav' : ''}`, children: [show && (_jsxs("nav", { className: "section-nav", "aria-label": t('g.common.onThisPage'), children: [_jsx(Eyebrow, { as: "p", children: t('g.common.onThisPage') }), _jsx("ol", { children: items.map((i) => {
                            const Icon = iconFor(keyOf(i.id));
                            return (_jsx("li", { children: _jsxs("a", { href: `#${i.id}`, onClick: (e) => go(e, i.id), className: active === i.id ? 'active' : undefined, children: [_jsx(Icon, { size: 20 }), _jsx("span", { children: i.label })] }) }, i.id));
                        }) })] })), _jsx("div", { className: "section-nav-content", ref: ref, children: children })] }));
};
export default SectionNav;
export { iconFor as sectionIconFor };
